import {
  SFFProblemType,
  TagText,
  ResponseDefinition,
  Skill,
  TagData,
} from './page-data';
import { addPeriod, removePeriod, capitalizeFirstCharacter } from '..';

const generateOptionText = (
  type: SFFProblemType,
  responseDefinition: ResponseDefinition
): string => {
  let ret: string;
  const { text: tagText, content } = responseDefinition;

  switch (type) {
    case SFFProblemType.Type1:
      {
        const text =
          typeof tagText === 'string' ? tagText : (tagText as TagText).gerund;
        ret = addPeriod(capitalizeFirstCharacter(text));
      }
      break;

    case SFFProblemType.Type4:
    case SFFProblemType.Type6:
    case SFFProblemType.Type2:
      {
        const text = content?.text;

        ret = text ? addPeriod(text) : '';
      }
      break;

    case SFFProblemType.Type7:
      ret = typeof tagText === 'string' ? tagText : 'Unknown';
      break;

    case SFFProblemType.Type3:
      {
        const text =
          typeof tagText === 'string'
            ? tagText
            : (tagText as TagText).declarative;
        ret = 'Because it ' + addPeriod(text.toLocaleLowerCase());
      }
      break;

    default: {
      const text =
        typeof tagText === 'string'
          ? tagText
          : (tagText as TagText).declarative;
      ret = addPeriod(text.toLocaleLowerCase());
    }
  }
  return ret;
};

function concatReason(reason: TagText[] | undefined): string | undefined {
  if (!reason) return;

  switch (reason.length) {
    case 0:
      return '';
    case 1:
      return removePeriod(reason[0].declarative);
    case 2:
      return (
        removePeriod(reason[0].declarative) +
        ' and ' +
        removePeriod(reason[1].declarative)
      );
    default: {
      const ret = reason.reduce((a: string, c, i): string => {
        const t =
          removePeriod(c.declarative) +
          (i === reason.length - 1
            ? ''
            : i === reason.length - 2
            ? ', and '
            : ', ');
        const ret = a + t;
        return ret;
      }, '');
      return ret;
    }
  }
}

export class ProblemOption {
  private _responseDefinition: ResponseDefinition;
  private _id: string;
  private _skill: Skill;
  private _text: string;
  private _problemQuality: boolean;
  private _isCorrect = false;
  private _isSelected = false;
  private _reason?: TagText[];
  private _problemType: SFFProblemType;
  private _tags: TagData[];

  public get tags(): TagData[] {
    return this._tags;
  }
  public get id(): string {
    return this._id;
  }
  public get text(): string {
    return this._text;
  }
  public get isSelected(): boolean {
    return this._isSelected;
  }
  public set isSelected(value: boolean) {
    this._isSelected = value;
  }
  public get isCorrectResponse(): boolean {
    return this._isCorrect;
  }
  public get isAnsweredCorrectly(): boolean {
    return this._isCorrect === this._isSelected;
  }
  public get reason(): TagText[] | undefined {
    return this._reason;
  }

  /**
   * Generate the text that appears when the user gets a question incorrect. This text is what
   * shows up in the review button.
   * 
   * Structure of the feedback highly depends on the question type and can incorporate custom
   * feedback.
   */
  public get reasonIncorrect(): string | null {  
    let ret: string | null;

    const reason = concatReason(this._reason);

    if (!reason) return null;

    switch (this._problemType) {
      case SFFProblemType.Type1:
        ret = reason;
        break;

      case SFFProblemType.Type2:
        ret = 'This statement';
        ret +=
          this._problemQuality !== this._isCorrect
            ? ' does not show '
            : ' shows ';
        ret += this._skill.name + ' because it ';
        ret += addPeriod(reason.toLocaleLowerCase());

        // Appending custom feedback after generic response because
        // the generic content explains why a response is good/poor
        if (this._responseDefinition.customReviewFeedback) {
          ret += " " + this._responseDefinition.customReviewFeedback;
        }
        break;

      case SFFProblemType.Type3:
        if (this._id !== 'SYSTEM') {
          ret = reason;

          // Appending custom feedback after generic response because
          // the generic content at least explains why a skill component/tag is good/poor
          if (this._responseDefinition.customReviewFeedback) {
            ret += " " + this._responseDefinition.customReviewFeedback;
          }
        } else {
          ret = 'None of the options show ' + this._skill.name + '.';
        }
        break;

      case SFFProblemType.Type4:
        // This question type gives a skill component and asks the user to pick a provider response
        // Custom feedback will explain why the response does or doesn't apply to the component so
        // no extra text needed
        if (!this._responseDefinition.customReviewFeedback) {
          ret = 'This response ';
          ret += addPeriod(reason.toLocaleLowerCase());
        } else {
          ret = this._responseDefinition.customReviewFeedback;
        }
        break;

      case SFFProblemType.Type7:
        ret = 'This statement should';
        ret += !this._isCorrect ? ' not' : '';
        ret += ' be removed because it ';
        ret += addPeriod(reason.toLocaleLowerCase());
        break;

      case SFFProblemType.Type6:
        {
          const text =
            'This statement' +
            (this._problemQuality === this._isCorrect
              ? ' shows'
              : ' does not show') +
            ' the skill ' +
            this._skill.name.toLocaleLowerCase() +
            ' because it ' +
            concatReason(this._reason);
          ret = addPeriod(text);
          // Append custom feedback
          if (this._responseDefinition.customReviewFeedback) {
            ret = ret + " " + this._responseDefinition.customReviewFeedback;
          }
        }
        break;

      default:
        ret = null;
      // if (this._id !== 'SYSTEM') {
      //   ret += 'This response';
      //   ret +=
      //     this._problemQuality !== this._isSelected
      //       ? ' shows '
      //       : ' does not show ';
      // } else {
      //   ret += 'None of the options show ';
      // }
      // ret += this._skill.name + '.';
    }

    return ret;
  }

  public getRawData(): ResponseDefinition {
    const responseDefinition = this._responseDefinition;
    responseDefinition.selected = this._isSelected;

    return responseDefinition;
  }

  // TODO:
  // The 'reason' in the response definition should be altered
  public constructor(
    responseDefinition: ResponseDefinition,
    problemQuality: boolean,
    problemType: SFFProblemType,
    skill: Skill
  ) {
    const { reason, id, correct, selected, tagData } = responseDefinition;

    this._responseDefinition = responseDefinition;
    this._text = generateOptionText(problemType, responseDefinition);
    this._reason = reason;
    this._skill = skill;
    this._id = id;
    this._problemQuality = problemQuality;
    this._problemType = problemType;
    this._isCorrect = correct;
    this._isSelected = selected || false;
    this._tags = tagData;
  }
}
